/**
 * Busca preditiva do cabeçalho (computador e modal do celular):
 * - ao focar o campo vazio, mostra as buscas populares (vêm prontas no HTML, configuráveis
 *   em Configurações do tema > Busca);
 * - ao digitar (com debounce), busca produtos/coleções/sugestões via a Predictive Search API
 *   da Shopify e pede o HTML já pronto pela Section Rendering API (section_id=predictive-search),
 *   reaproveitando os mesmos snippets (preço, imagem) do resto do tema;
 * - setas/Enter/Esc navegam pelo painel; clique fora ou Esc fecha.
 * Sem JavaScript, o formulário continua funcionando: envia para a página de resultados normal.
 */
(() => {
  if (customElements.get('predictive-search')) return;

  const MIN_LENGTH = 2;
  const DEBOUNCE_MS = 300;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';

      this.input = this.querySelector('[data-predictive-search-input]');
      this.panel = this.querySelector('[data-predictive-search-panel]');
      this.results = this.querySelector('[data-predictive-search-results]');
      this.popular = this.querySelector('[data-predictive-search-popular]');
      this.status = this.querySelector('[data-predictive-search-status]');
      this.resetButton = this.querySelector('[data-predictive-search-reset]');
      if (!this.input || !this.panel || !this.results) return;

      this.limit = Number(this.dataset.productLimit) || 5;
      this.lastQuery = null;
      this.controller = null;
      this.debounceId = null;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => this.onFocus());
      this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      this.addEventListener('keydown', (event) => this.onPanelKeydown(event));

      if (this.resetButton) {
        this.resetButton.addEventListener('click', () => this.reset());
      }

      this.boundOutsideClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      document.addEventListener('click', this.boundOutsideClick);

      this.toggleReset();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundOutsideClick);
    }

    toggleReset() {
      if (this.resetButton) this.resetButton.hidden = this.input.value.trim() === '';
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.clearActive();
    }

    reset() {
      this.input.value = '';
      this.lastQuery = '';
      this.results.innerHTML = '';
      this.toggleReset();
      this.showPopular();
      this.input.focus();
    }

    showPopular() {
      if (this.popular) this.popular.hidden = false;
      this.results.hidden = true;
      this.open();
    }

    onFocus() {
      const value = this.input.value.trim();
      if (value === '') {
        this.showPopular();
        return;
      }
      if (value === this.lastQuery && this.results.innerHTML !== '') {
        if (this.popular) this.popular.hidden = true;
        this.results.hidden = false;
        this.open();
        return;
      }
      this.search(value);
    }

    onInput() {
      this.toggleReset();
      const value = this.input.value.trim();
      window.clearTimeout(this.debounceId);

      if (value === '') {
        this.lastQuery = '';
        this.results.innerHTML = '';
        this.showPopular();
        return;
      }

      if (value.length < MIN_LENGTH) return;

      this.debounceId = window.setTimeout(() => this.search(value), DEBOUNCE_MS);
    }

    async search(value) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.lastQuery = value;

      if (this.popular) this.popular.hidden = true;
      this.results.hidden = false;
      this.results.setAttribute('aria-busy', 'true');
      this.open();

      // Sem ".json": assim a Shopify manda de volta o HTML já renderizado pela seção
      // (Section Rendering API), em vez do JSON cru dos recursos.
      const params = new URLSearchParams({
        q: value,
        'resources[type]': 'product,collection,query',
        'resources[limit]': String(this.limit),
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search',
      });

      try {
        const response = await fetch(`/search/suggest?${params.toString()}`, {
          signal: this.controller.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(String(response.status));
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.getElementById('shopify-section-predictive-search');
        const html = section ? section.innerHTML : '';

        // Uma resposta que chegou atrasada, para um termo que já não é o que está no campo,
        // não deve substituir o que a pessoa já está vendo.
        if (this.input.value.trim() !== value) return;

        this.results.innerHTML = html || '';
        this.clearActive();
        if (this.status) {
          const count = this.results.querySelectorAll('[data-predictive-search-option]').length;
          this.status.textContent = count > 0 ? `${count} resultados para ${value}` : `Nenhum resultado para ${value}`;
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.results.innerHTML = '';
        }
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }

    getOptions() {
      return Array.from(this.querySelectorAll('[data-predictive-search-option]')).filter((el) => !el.closest('[hidden]'));
    }

    clearActive() {
      this.querySelectorAll('[data-predictive-search-option].is-active').forEach((el) => el.classList.remove('is-active'));
    }

    onInputKeydown(event) {
      if (event.key === 'ArrowDown') {
        const options = this.getOptions();
        if (options.length === 0) return;
        event.preventDefault();
        this.open();
        options[0].focus();
        return;
      }
      if (event.key === 'Escape') {
        if (!this.panel.hidden) {
          event.preventDefault();
          this.close();
        } else if (this.input.value !== '') {
          this.reset();
        }
      }
    }

    onPanelKeydown(event) {
      const target = event.target instanceof Element ? event.target.closest('[data-predictive-search-option]') : null;
      if (!target) return;

      const options = this.getOptions();
      const index = options.indexOf(target);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const next = options[index + 1];
        if (next) next.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (index <= 0) {
          this.input.focus();
        } else {
          options[index - 1].focus();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        this.input.focus();
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);

  // Modal de busca no celular: o ícone do cabeçalho abre um <dialog> com o mesmo componente
  // (com autofoco), do mesmo jeito que a gaveta do carrinho (assets/cart-drawer.js).
  const drawer = document.querySelector('[data-search-drawer]');
  if (drawer) {
    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-search-open]')) {
        event.preventDefault();
        if (!drawer.open) drawer.showModal();
        return;
      }

      if (target.closest('[data-search-close]')) {
        event.preventDefault();
        if (drawer.open) drawer.close();
        return;
      }

      if (target === drawer) {
        const rect = drawer.getBoundingClientRect();
        const outside =
          event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
        if (outside) drawer.close();
      }
    });
  }
})();
